import React, { useState, useEffect } from 'react';
import { UserSettings, PaymentMethod } from '../types';
import { storageService } from '../services/storageService';
import { PAYMENT_DETAILS } from '../constants';
import { normalizeNumber } from '../utils/numberUtils';
import { X, Save, Loader2, Wallet, Settings as SettingsIcon, CheckCircle2, Circle, ChevronDown, ChevronRight, MinusCircle } from 'lucide-react';

interface SettingsProps {
  userId: string;
  onClose: () => void;
}

export const Settings: React.FC<SettingsProps> = ({ userId, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<UserSettings>({
    monthlyBudget: 0,
    balanceSettings: {
      enabled: false,
      displayTotal: false,
      includedMethods: [],
      balances: {}
    }
  });
  const [expandedMethods, setExpandedMethods] = useState<string[]>([]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await storageService.getSettings(userId);
        // Merge with defaults to ensure all fields exist
        // Default behavior: Include all methods AND all details if not set
        const allMethods = Object.values(PaymentMethod);
        const allDetails = allMethods.flatMap(method => {
            const details = PAYMENT_DETAILS[method] || [];
            return details.map(detail => getBalanceKey(method, detail));
        });
        const defaultIncludedMethods = [...allMethods, ...allDetails];
        
        setSettings({
          monthlyBudget: data.monthlyBudget || 0,
          balanceSettings: {
            enabled: data.balanceSettings?.enabled || false,
            displayTotal: data.balanceSettings?.displayTotal || false,
            includedMethods: data.balanceSettings?.includedMethods || defaultIncludedMethods,
            balances: data.balanceSettings?.balances || {}
          }
        });
      } catch (e) {
        console.error("Failed to load settings", e);
      } finally {
        setIsLoading(false);
      }
    };
    loadSettings();
  }, [userId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await storageService.updateSettings(userId, settings);
      onClose();
    } catch (e) {
      console.error("Failed to save settings", e);
      alert("設定の保存に失敗しました。");
    } finally {
      setIsSaving(false);
    }
  };

  const getBalanceKey = (method: string, detail?: string) => {
      if (detail && detail !== 'その他') return `${method}:${detail}`;
      return method;
  };

  const toggleMethod = (methodKey: string) => {
    const currentMethods = settings.balanceSettings?.includedMethods || [];
    let newMethods = [...currentMethods];

    // Check if this is a base method (e.g., PREPAID_CARD)
    const isBaseMethod = !methodKey.includes(':');
    
    if (isBaseMethod) {
        // Find all details for this base method
        const details = PAYMENT_DETAILS[methodKey as PaymentMethod] || [];
        const detailKeys = details.map(d => getBalanceKey(methodKey, d)).filter(k => k !== methodKey);
        
        const isCurrentlyIncluded = currentMethods.includes(methodKey);
        
        if (isCurrentlyIncluded) {
            // Remove base method AND all its details
            newMethods = newMethods.filter(m => m !== methodKey && !detailKeys.includes(m));
        } else {
            // Add base method AND all its details
            // Filter out duplicates just in case
            const toAdd = [methodKey, ...detailKeys].filter(k => !newMethods.includes(k));
            newMethods = [...newMethods, ...toAdd];
        }
    } else {
        // Toggle individual detail
        if (newMethods.includes(methodKey)) {
            newMethods = newMethods.filter(m => m !== methodKey);
        } else {
            newMethods.push(methodKey);
        }
        
        // Check parent status
        const [parentMethod] = methodKey.split(':');
        const parentDetails = PAYMENT_DETAILS[parentMethod as PaymentMethod] || [];
        const parentDetailKeys = parentDetails.map(d => getBalanceKey(parentMethod, d)).filter(k => k !== parentMethod);
        
        // Count how many details are selected
        const selectedDetailsCount = parentDetailKeys.filter(k => newMethods.includes(k)).length;
        
        // Logic for parent checkbox:
        // If ALL details are selected -> Select parent
        // If NO details are selected -> Deselect parent
        // If SOME details are selected -> Select parent (to show indeterminate state visually)
        
        // Actually, let's keep parent selected if ANY detail is selected, 
        // or if the user explicitly selected the parent (which we can't track easily without more state).
        // Let's simplify: If any detail is selected, ensure parent is selected.
        // If no details are selected, ensure parent is deselected.
        // Wait, the user might want to select the parent (aggregate) without details? Unlikely for "detailed view".
        
        if (selectedDetailsCount > 0) {
            if (!newMethods.includes(parentMethod)) {
                newMethods.push(parentMethod);
            }
        } else {
            // If no details selected, remove parent too? 
            // Or keep it if it was manually selected? 
            // Let's remove it to keep it clean.
            newMethods = newMethods.filter(m => m !== parentMethod);
        }
    }
    
    // Deduplicate before saving
    newMethods = Array.from(new Set(newMethods));

    setSettings(prev => ({
      ...prev,
      balanceSettings: {
        ...prev.balanceSettings!,
        includedMethods: newMethods
      }
    }));
  };

  const updateBalance = (methodKey: string, value: string) => {
    // Allow full-width numbers
    const numValue = normalizeNumber(value);
    
    setSettings(prev => ({
      ...prev,
      balanceSettings: {
        ...prev.balanceSettings!,
        balances: {
          ...prev.balanceSettings!.balances,
          [methodKey]: numValue
        }
      }
    }));
  };

  const toggleExpand = (method: string) => {
      setExpandedMethods(prev => 
          prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
      );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-7 h-7 animate-spin" style={{color:'var(--forest)'}} />
      </div>
    );
  }

  return (
    <div className="overflow-hidden h-full flex flex-col" style={{background:'var(--cream)'}}>
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between shrink-0 animate-fade-down"
        style={{borderBottom:'1px solid rgba(44,36,22,0.08)', background:'white'}}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center hover-lift" style={{background:'var(--forest-pale)'}}>
            <SettingsIcon className="w-4 h-4 animate-spin-slow" style={{color:'var(--forest)'}} />
          </div>
          <h2 className="font-semibold" style={{color:'var(--ink)'}}>設定</h2>
        </div>
        <button onClick={onClose}
          className="p-2 rounded-xl transition-transform duration-200 hover:rotate-90 active:scale-90"
          style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 space-y-6 overflow-y-auto flex-1 scrollbar-hide">

        {/* Budget */}
        <section className="stagger-child animate-fade-up">
          <h3 className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{color:'var(--ink-muted)'}}>基本設定</h3>
          <div className="card p-4 card-hover">
            <label className="block text-sm font-semibold mb-2" style={{color:'var(--ink)'}}>月次予算目標</label>
            <div className="relative">
              <input
                type="number"
                value={settings.monthlyBudget}
                onChange={e => setSettings({...settings, monthlyBudget: parseInt(e.target.value) || 0})}
                className="input-warm w-full pl-4 pr-10 py-3 text-lg font-mono font-500"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold" style={{color:'var(--ink-muted)'}}>円</span>
            </div>
          </div>
        </section>

        {/* Balance Management */}
        <section className="stagger-child animate-fade-up delay-100">
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-[10px] font-bold uppercase tracking-widest" style={{color:'var(--ink-muted)'}}>資産・残高管理</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold transition-colors duration-300"
                style={{color: settings.balanceSettings?.enabled ? 'var(--forest)' : 'var(--ink-muted)'}}>
                {settings.balanceSettings?.enabled ? '有効' : '無効'}
              </span>
              <button
                onClick={() => setSettings(prev => ({
                  ...prev,
                  balanceSettings: {...prev.balanceSettings!, enabled: !prev.balanceSettings!.enabled}
                }))}
                className="relative w-11 h-6 rounded-full"
                style={{
                  background: settings.balanceSettings?.enabled ? 'var(--forest)' : 'rgba(44,36,22,0.15)',
                  transition: 'background 0.3s ease'
                }}
              >
                <span className="absolute top-1 w-4 h-4 bg-white rounded-full shadow"
                  style={{
                    left: settings.balanceSettings?.enabled ? '24px' : '4px',
                    transition: 'left 0.25s cubic-bezier(0.34,1.56,0.64,1)'
                  }} />
              </button>
            </div>
          </div>

          {settings.balanceSettings?.enabled && (
            <div className="space-y-3 animate-slide-down">
              <div className="card p-4 card-hover animate-fade-up">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold" style={{color:'var(--ink)'}}>合計残高をトップに表示</label>
                  <button
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      balanceSettings: {...prev.balanceSettings!, displayTotal: !prev.balanceSettings!.displayTotal}
                    }))}
                    className="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all"
                    style={settings.balanceSettings?.displayTotal
                      ? {background:'var(--forest)', borderColor:'var(--forest)', color:'white'}
                      : {borderColor:'rgba(44,36,22,0.2)', color:'transparent'}}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs mt-2" style={{color:'var(--ink-muted)'}}>
                  選択した決済方法の合計をホーム画面に表示します。
                </p>
              </div>

              <div className="card overflow-hidden">
                <div className="px-4 py-3 flex items-center gap-2" style={{borderBottom:'1px solid rgba(44,36,22,0.07)', background:'var(--cream)'}}>
                  <Wallet className="w-4 h-4" style={{color:'var(--ink-muted)'}} />
                  <span className="text-xs font-bold" style={{color:'var(--ink-muted)'}}>決済方法と残高設定</span>
                </div>
                <div>
                  {Object.values(PaymentMethod).filter(m => m !== PaymentMethod.POINTS && m !== PaymentMethod.OTHER).map((method, idx) => {
                    const details = PAYMENT_DETAILS[method] || [];
                    const hasDetails = details && details.length > 0;
                    const isExpanded = expandedMethods.includes(method);
                    const methodKey = method;
                    const detailKeys = details.map(d => getBalanceKey(method, d)).filter(k => k !== method);
                    const currentMethods = settings.balanceSettings?.includedMethods || [];
                    const selectedDetailsCount = detailKeys.filter(k => currentMethods.includes(k)).length;
                    const totalDetailsCount = detailKeys.length;
                    const isPartiallySelected = hasDetails && selectedDetailsCount > 0 && selectedDetailsCount < totalDetailsCount;
                    const isSelected = currentMethods.includes(methodKey);
                    const parentOwnBalance = settings.balanceSettings?.balances[methodKey] || 0;
                    const childrenBalance = detailKeys.reduce((sum, key) => sum + (settings.balanceSettings?.balances[key] || 0), 0);
                    const displayBalance = hasDetails ? (parentOwnBalance + childrenBalance) : parentOwnBalance;

                    return (
                      <div key={method}
                        className="stagger-child animate-slide-right"
                        style={{
                          borderTop: idx===0?'none':'1px solid rgba(44,36,22,0.06)',
                          animationDelay:`${idx*0.04}s`
                        }}>
                        <div className="px-4 py-3 flex items-center gap-3 transition-all duration-200 hover:bg-[var(--cream)]"
                          style={{background:'white'}}>
                          {hasDetails ? (
                            <button onClick={() => toggleExpand(method)} className="shrink-0 transition-transform duration-200"
                              style={{color:'var(--ink-muted)', transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}}>
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          ) : <div className="w-4 shrink-0" />}

                          <button onClick={() => toggleMethod(methodKey)}
                            className="shrink-0 transition-all duration-200 active:scale-90 hover:scale-110"
                            style={{color: isSelected||isPartiallySelected ? 'var(--forest)' : 'rgba(44,36,22,0.2)'}}>
                            {isPartiallySelected
                              ? <MinusCircle className="w-5 h-5" />
                              : isSelected
                                ? <CheckCircle2 className="w-5 h-5 animate-bounce-in" />
                                : <Circle className="w-5 h-5" />}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold" style={{color:'var(--ink)'}}>{method}</div>
                          </div>

                          <div className="w-28 relative shrink-0">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={displayBalance}
                              onChange={e => {
                                const newTotal = normalizeNumber(e.target.value);
                                const newParentOwn = hasDetails ? (newTotal - childrenBalance) : newTotal;
                                updateBalance(methodKey, newParentOwn.toString());
                              }}
                              className="input-warm w-full py-1.5 px-2 text-right text-sm font-mono"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        {hasDetails && isExpanded && (
                          <div className="pl-12 pr-4 py-2 space-y-1.5 animate-slide-down"
                            style={{borderTop:'1px dashed rgba(44,36,22,0.08)', background:'var(--cream)'}}>
                            {details.map((detail, dIdx) => {
                              if (detail === 'その他') return null;
                              const detailKey = getBalanceKey(method, detail);
                              const isDetailSelected = settings.balanceSettings?.includedMethods.includes(detailKey);
                              return (
                                <div key={detailKey}
                                  className="flex items-center gap-3 py-1.5 stagger-child animate-slide-right"
                                  style={{borderBottom:'1px solid rgba(44,36,22,0.05)', animationDelay:`${dIdx*0.05}s`}}>
                                  <button onClick={() => toggleMethod(detailKey)}
                                    className="shrink-0 transition-all duration-200 active:scale-90 hover:scale-110"
                                    style={{color: isDetailSelected ? 'var(--forest)' : 'rgba(44,36,22,0.2)'}}>
                                    {isDetailSelected
                                      ? <CheckCircle2 className="w-4 h-4 animate-bounce-in" />
                                      : <Circle className="w-4 h-4" />}
                                  </button>
                                  <div className="flex-1 text-sm" style={{color:'var(--ink-light)'}}>{detail}</div>
                                  <div className="w-24">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={settings.balanceSettings?.balances[detailKey] ?? 0}
                                      onChange={e => updateBalance(detailKey, e.target.value)}
                                      className="input-warm w-full py-1.5 px-2 text-right text-xs font-mono"
                                      placeholder="0"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Save Button */}
      <div className="p-4 shrink-0" style={{borderTop:'1px solid rgba(44,36,22,0.08)', background:'white'}}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn-forest w-full py-3.5 flex items-center justify-center gap-2 ripple-container"
          style={{transition:'all 0.3s cubic-bezier(0.34,1.56,0.64,1)'}}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
        >
          {isSaving
            ? <Loader2 className="w-5 h-5 animate-spin" />
            : <Save className="w-5 h-5 transition-transform duration-200 hover:rotate-12" />}
          設定を保存
        </button>
      </div>
    </div>
  );
};
